import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';

import { useBlocker, useParams } from 'react-router';

import { ConfirmModal } from 'components/modal';
import { useFormContext } from 'context/FormContext';
import { useQueryConfigPedidoSteps } from 'pages/compra/configuracao-pedido/hooks/useQueryConfigPedidoSteps';
import { ENABLED_STEPS_EDIT, STEP_LIST } from 'pages/compra/configuracao-pedido/utils';

export type ConfigPedidoStep = {
    dsChaveConfigPedidoStep: string;
    dsConfigPedidoStep: string;
    fgSaved: boolean;
    fgDirty: boolean;
    fgDisabled: boolean;
    component: ReactNode;
};

type ConfigPedidoStepContextData = {
    activeStep: ConfigPedidoStep;
    stepList: ConfigPedidoStep[];
    setStepDisabled(dsChave: string, fgDisabled: boolean): void;
    setActiveStepSaved(): void;
    setActiveStepDirty(dirty?: boolean): void;
    requestStepChange(step: ConfigPedidoStep): void;
};

const ConfigPedidoStepContext = createContext<ConfigPedidoStepContextData>({} as ConfigPedidoStepContextData);

export function ConfigPedidoStepProvider({ children }: { children: ReactNode }) {
    const [activeStep, setActiveStep] = useState(STEP_LIST[0]); // 0 = dadosBasicos
    const [stepList, setStepList] = useState<ConfigPedidoStep[]>(STEP_LIST);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const pendingStep = useRef<ConfigPedidoStep | null>(null);
    const { id } = useParams();
    const { data } = useQueryConfigPedidoSteps();
    const { state, proceed, reset } = useBlocker(activeStep.fgDirty);

    /**
     * Atualiza as steps que já estão salvas.
     */
    const updateSavedSteps = () => {
        if (data) {
            setStepList((list) =>
                list.map((step) => {
                    const match = data?.find((item) => item.dsChave === step.dsChaveConfigPedidoStep);

                    return { ...step, fgSaved: match?.dsValor ?? false };
                }),
            );

            const activeStepSaved = data?.find((item) => item.dsChave === activeStep.dsChaveConfigPedidoStep)?.dsValor ?? false;

            setActiveStep((prevActiveStep) => ({ ...prevActiveStep, fgSaved: activeStepSaved }));
        }
    };

    /**
     * Atualiza as steps que estão habilitadas para edição por padrão.
     */
    const updateDisabledSteps = () => {
        if (id) {
            setStepList((list) => list.map((step) => ({ ...step, fgDisabled: !ENABLED_STEPS_EDIT.includes(step.dsChaveConfigPedidoStep) })));
        }
    };

    /**
     * Salva o step atual.
     */
    const setActiveStepSaved = () => {
        setStepList((list) =>
            list.map((step) => {
                if (step.dsChaveConfigPedidoStep === activeStep.dsChaveConfigPedidoStep) return { ...step, fgSaved: true, fgDirty: false };

                return step;
            }),
        );

        setActiveStep((prevActiveStep) => ({ ...prevActiveStep, fgSaved: true, fgDirty: false }));
    };

    /**
     * Suja o step atual.
     */
    const setActiveStepDirty = (fgDirty = true) => {
        setStepList((list) =>
            list.map((step) => {
                if (step.dsChaveConfigPedidoStep === activeStep.dsChaveConfigPedidoStep) return { ...step, fgDirty };

                return step;
            }),
        );

        setActiveStep((prevActiveStep) => ({ ...prevActiveStep, fgDirty }));
    };

    /**
     * Solicita a mudança de step.
     *
     * Caso o step atual esteja sujo, exibe um modal de confirmação.
     */
    const requestStepChange = (step: ConfigPedidoStep) => {
        if (activeStep.dsChaveConfigPedidoStep === step.dsChaveConfigPedidoStep) return;

        if (activeStep.fgDirty) {
            pendingStep.current = step;

            setShowConfirmModal(true);
        } else {
            setActiveStep(step);
        }
    };

    /**
     * Confirma a mudança de step.
     *
     * Se o usuário está saindo da tela, prossegue com a mudança.
     *
     * Caso contrário, limpa o conteúdo do contexto de form, define o step atual como salvo e sujo e define o step pendente como ativo.
     */
    const confirmStepChange = () => {
        if (state === 'blocked') {
            proceed();

            return;
        }

        setActiveStepDirty(false);

        if (pendingStep.current) {
            setActiveStep(pendingStep.current);

            pendingStep.current = null;
        }
    };

    /**
     * Desabilita ou habilita um step.
     */
    const setStepDisabled = (dsChave: string, fgDisabled: boolean) => {
        setStepList((list) =>
            list.map((step) => {
                if (step.dsChaveConfigPedidoStep === dsChave) return { ...step, fgDisabled };

                return step;
            }),
        );
    };

    useEffect(updateSavedSteps, [data]);

    useEffect(updateDisabledSteps, [id]);

    return (
        <ConfigPedidoStepContext.Provider
            value={{
                activeStep,
                stepList,
                setActiveStepSaved,
                setStepDisabled,
                setActiveStepDirty,
                requestStepChange,
            }}
        >
            <ConfirmModal
                open={showConfirmModal || state === 'blocked'}
                setOpen={setShowConfirmModal}
                text="Ao sair ou trocar de configuração, alterações não salvas serão perdidas. Gostaria de continuar?"
                confirm={confirmStepChange}
                cancel={() => {
                    if (state === 'blocked') {
                        reset();
                    }
                }}
            />

            {children}
        </ConfigPedidoStepContext.Provider>
    );
}

export const useConfigPedidoStepContext = () => useContext(ConfigPedidoStepContext);
